'use client';

import type { ReactNode } from 'react';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import XIcon from '@mui/icons-material/X';
import { styled } from '@mui/material/styles';
import { COLORS, FONTS, MEDIA } from 'components/HomepageV3/theme/tokens';
import { withSe3AssetPrefix } from 'components/HomepageV3/assetPrefix';
import {
  buildSe3ExploreFilterRoute,
  useSe3QaSearch,
  withSe3QaOverride,
} from 'components/HomepageV3/utils/routes';
import { socialMenu } from 'utils/links';

const APP_STORE_HREF = 'https://apps.apple.com/us/app/startengine-startup-investing/id1560434961';
const GOOGLE_PLAY_HREF =
  'https://play.google.com/store/apps/details?id=com.startengine.android&hl=en_US';

export interface FooterLinkColumn {
  /** Column heading (gold, tracked uppercase). */
  heading: string;
  /** Links rendered under the heading. */
  links: Array<{ label: string; href: string }>;
}

export interface EditorialFooterProps {
  /** Override the link columns. Defaults to the editorial source columns. */
  columns?: FooterLinkColumn[];
  /** Override the brand mission line. */
  mission?: ReactNode;
  /** Override the copyright line. */
  copyright?: ReactNode;
  /**
   * Override the "Important Message" legal block. The default copy is
   * US-specific and states that offerings are not made to Canadian residents,
   * so any jurisdiction-specific surface must supply its own.
   */
  legal?: ReactNode;
  /**
   * Show the App Store / Google Play badges under the brand column. Pass false
   * on surfaces where the StartEngine app is not offered (e.g. Canada).
   */
  showAppBadges?: boolean;
  /**
   * Show the brand / link-column row above the legal block. Pass false on
   * surfaces that render only the regulatory disclosure (e.g. Canada).
   */
  showBrandRow?: boolean;
  className?: string;
  'data-cy'?: string;
}

const DEFAULT_COLUMNS: FooterLinkColumn[] = [
  {
    heading: 'Investing',
    links: [
      { label: 'Pre-IPO Funds', href: buildSe3ExploreFilterRoute('private') },
      { label: 'Startups', href: buildSe3ExploreFilterRoute('crowdfunding') },
      { label: 'Marketplace', href: '/marketplace' },
      { label: 'Offering Directory', href: '/explore/past-offerings' },
      { label: 'Blog', href: '/blog' },
    ],
  },
  {
    heading: 'Support',
    links: [
      { label: 'Help Center', href: 'https://isms.startengine.com/help' },
      { label: 'Contact Us', href: '/contact-us?source=footer' },
      { label: 'Browse Startups', href: buildSe3ExploreFilterRoute('crowdfunding') },
      { label: 'For Founders', href: '/raise-capital' },
    ],
  },
  {
    heading: 'Legal',
    links: [
      { label: 'Terms of Service', href: '/terms' },
      { label: 'Privacy Policy', href: '/privacy' },
      { label: 'Disclaimers', href: '/disclaimer' },
      { label: 'SEC Filings', href: '/annual-reports' },
      { label: 'Form CRS', href: '/form-crs' },
      { label: 'Reg. BI Disclosures', href: '/reg-bi-disclosure' },
      {
        label: 'Business Continuity Plan Disclosure',
        href: '/business-continuity-plan-disclosure-statement',
      },
    ],
  },
];

const FOOTER_WORDMARK_SRC = withSe3AssetPrefix('/homepage-v3/se-wordmark.png');

const SOCIAL_LINKS = [
  {
    label: 'Facebook',
    href: socialMenu.facebook,
    Icon: FacebookIcon,
  },
  {
    label: 'X',
    href: socialMenu.twitter,
    Icon: XIcon,
  },
  {
    label: 'LinkedIn',
    href: socialMenu.linkedIn,
    Icon: LinkedInIcon,
  },
  {
    label: 'Instagram',
    href: socialMenu.instagram,
    Icon: InstagramIcon,
  },
];

const AppleGlyph = (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M16.365 1.43c0 1.14-.42 2.2-1.12 2.99-.85.96-2.24 1.7-3.38 1.61-.14-1.1.43-2.27 1.07-3 .73-.83 2.02-1.46 3.1-1.55.02.32.02.63.02.95h.31zM20.5 17.04c-.55 1.27-.82 1.84-1.53 2.96-.99 1.56-2.39 3.5-4.12 3.51-1.54.02-1.94-1-4.03-.99-2.09.01-2.53 1.01-4.07.99-1.73-.02-3.05-1.77-4.04-3.33C-.04 16.6-.31 11.2 1.4 8.34c1.21-2.03 3.12-3.21 4.92-3.21 1.83 0 2.98 1 4.49 1 1.47 0 2.36-1 4.48-1 1.6 0 3.3.87 4.51 2.38-3.96 2.17-3.32 7.83.7 9.53z" />
  </svg>
);

const FooterRoot = styled('footer')({
  background: 'var(--night-deep)',
  borderTop: '1px solid rgba(233,215,182,0.14)',
  color: 'var(--c-text-2)',
  fontFamily: FONTS.sans,
});

const Inner = styled('div')({
  maxWidth: '1240px',
  margin: '0 auto',
  padding: `48px var(--pad) 36px`,
  [MEDIA.md]: {
    padding: `64px var(--pad) 44px`,
  },
});

const Top = styled('div')({
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: '44px',
  alignItems: 'start',
  '@media (min-width: 1081px)': {
    gridTemplateColumns: 'minmax(0, 1fr) 1.5fr',
    gap: '64px',
  },
});

const Brand = styled('div')({});

const Logo = styled('div')({
  display: 'flex',
  alignItems: 'center',
  marginBottom: '18px',
});

const LogoImg = styled('img')({
  height: 26,
  width: 'auto',
  display: 'block',
});

const Mission = styled('p')({
  fontSize: '16px',
  lineHeight: 1.7,
  color: 'var(--c-text-2)',
  maxWidth: '340px',
  margin: 0,
});

const MissionLink = styled('a')({
  color: 'inherit',
  textDecoration: 'underline',
  textUnderlineOffset: '2px',
  '&:hover': { color: 'var(--champagne)' },
});

const DEFAULT_MISSION = (
  <>
    Your destination for private market investing. Access pre-IPO companies and learn about{' '}
    <MissionLink href="/marketplace">Marketplace eligibility</MissionLink>; buyer demand, liquidity,
    and execution may not be available.
  </>
);

const Copy = styled('p')({
  marginTop: '18px',
  marginBottom: 0,
  fontSize: '13.5px',
  color: 'var(--c-text-3)',
});

const Badges = styled('div')({
  display: 'flex',
  gap: '12px',
  marginTop: '22px',
  flexWrap: 'wrap',
});

const SocialLinks = styled('nav')({
  display: 'flex',
  alignItems: 'center',
  gap: '15px',
  marginTop: '22px',
  flexWrap: 'wrap',
});

const SocialLink = styled('a')({
  display: 'inline-grid',
  placeItems: 'center',
  width: '34px',
  height: '34px',
  borderRadius: '999px',
  border: '1px solid rgba(233,215,182,0.28)',
  color: 'var(--c-text-2)',
  background: 'rgba(0,0,0,0.18)',
  textDecoration: 'none',
  transition: 'border-color 0.15s, color 0.15s, background 0.15s',
  '& svg': {
    width: '19px',
    height: '19px',
  },
  '&:hover': {
    borderColor: 'var(--champagne)',
    color: '#fff',
    background: 'rgba(0,0,0,0.36)',
  },
  '&:focus-visible': {
    outline: '2px solid var(--teal)',
    outlineOffset: '3px',
  },
});

const StoreBadge = styled('a')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '13px',
  padding: '13px 22px',
  borderRadius: '14px',
  border: '1px solid rgba(233,215,182,0.35)',
  background: 'rgba(0,0,0,0.25)',
  textDecoration: 'none',
  transition: 'border-color 0.15s, background 0.15s',
  '&:hover': { borderColor: 'var(--champagne)', background: 'rgba(0,0,0,0.4)' },
  '@media (max-width: 640px)': { flex: 1, justifyContent: 'center' },
});

const StoreGlyph = styled('span')({
  display: 'grid',
  placeItems: 'center',
  color: '#fff',
  width: '26px',
  height: '26px',
  flexShrink: 0,
  '& svg': { width: '24px', height: '24px', display: 'block' },
});

const PlayTri = styled('span')({
  width: 0,
  height: 0,
  borderStyle: 'solid',
  borderWidth: '11px 0 11px 18px',
  borderColor: 'transparent transparent transparent var(--champagne)',
  display: 'block',
});

const StoreText = styled('span')({
  display: 'flex',
  flexDirection: 'column',
  lineHeight: 1.15,
});

const StoreTop = styled('span')({
  fontSize: '11px',
  fontWeight: 500,
  letterSpacing: '0.04em',
  color: 'var(--c-text-2)',
  whiteSpace: 'nowrap',
});

const StoreBottom = styled('span')({
  fontFamily: FONTS.serif,
  fontSize: '20px',
  fontWeight: 400,
  color: '#fff',
  letterSpacing: '0.005em',
});

const Cols = styled('div')({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '28px 20px',
  [MEDIA.sm]: {
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '32px 24px',
  },
  '@media (min-width: 1081px)': {
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '32px',
  },
});

const Col = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '13px',
});

const ColHeading = styled('div')({
  fontSize: '11px',
  fontWeight: 600,
  letterSpacing: '0.22em',
  textTransform: 'uppercase',
  color: COLORS.goldOnDark,
  marginBottom: '5px',
});

const ColLink = styled('a')({
  fontSize: '15.5px',
  color: 'var(--c-text-2)',
  textDecoration: 'none',
  transition: 'color 0.15s',
  '&:hover': { color: 'var(--champagne)' },
});

const Rule = styled('div')({
  height: '1px',
  background: 'rgba(233,215,182,0.14)',
  margin: '44px 0 28px',
});

const Legal = styled('div')({
  fontSize: '13px',
  lineHeight: 1.5,
  color: 'var(--c-text-3)',
  maxWidth: '1000px',
  textWrap: 'pretty',
  '& p': { margin: '0 0 11px' },
  '& a': {
    color: 'var(--teal)',
    fontWeight: 600,
    textDecoration: 'none',
    transition: 'color 0.15s',
    '&:hover': { color: 'var(--champagne)', textDecoration: 'underline' },
  },
});

const LegalH = styled('h2')({
  fontFamily: FONTS.serif,
  fontWeight: 400,
  fontSize: '19px',
  color: 'var(--c-text-2)',
  margin: '0 0 16px',
});

const LegalSub = styled('h3')({
  fontFamily: FONTS.serif,
  fontWeight: 400,
  fontSize: '17px',
  color: 'var(--c-text-2)',
  margin: '26px 0 14px',
});

const LegalList = styled('ul')({
  margin: '0 0 11px',
  paddingLeft: '20px',
  display: 'flex',
  flexDirection: 'column',
  gap: '7px',
  '& li': { paddingLeft: '4px' },
});

/**
 * Editorial homepage footer (`.se-footer`). Dark night-deep surface with the
 * StartEngine wordmark, brand mission, optional app-store badges, three link
 * columns (Investing / Support / Legal) and the full regulatory disclosure
 * block. Ported 1:1 from `design-refs/source/private-home.css` +
 * `private-home.js` (`buildFooter`).
 */
export function EditorialFooter({
  columns = DEFAULT_COLUMNS,
  mission = DEFAULT_MISSION,
  copyright = '© 2026 All Rights Reserved',
  legal,
  showAppBadges = true,
  showBrandRow = true,
  className,
  'data-cy': dataCy = 'editorial-footer',
}: EditorialFooterProps) {
  const qaSearch = useSe3QaSearch();
  return (
    <FooterRoot className={className} data-cy={dataCy}>
      <Inner>
        {showBrandRow ? (
          <>
            <Top>
              <Brand>
                <Logo data-cy="editorial-footer-brand">
                  <LogoImg src={FOOTER_WORDMARK_SRC} alt="StartEngine" />
                </Logo>
                <Mission>{mission}</Mission>
                <Copy>{copyright}</Copy>
                {showAppBadges ? (
                  <Badges data-cy="editorial-footer-badges">
                    <StoreBadge href={APP_STORE_HREF} aria-label="Download on the App Store">
                      <StoreGlyph>{AppleGlyph}</StoreGlyph>
                      <StoreText>
                        <StoreTop>Download on the</StoreTop>
                        <StoreBottom>App Store</StoreBottom>
                      </StoreText>
                    </StoreBadge>
                    <StoreBadge href={GOOGLE_PLAY_HREF} aria-label="Get it on Google Play">
                      <StoreGlyph>
                        <PlayTri />
                      </StoreGlyph>
                      <StoreText>
                        <StoreTop>Get it on</StoreTop>
                        <StoreBottom>Google Play</StoreBottom>
                      </StoreText>
                    </StoreBadge>
                  </Badges>
                ) : null}
                <SocialLinks
                  data-cy="editorial-footer-social"
                  aria-label="StartEngine social links"
                >
                  {SOCIAL_LINKS.map(({ label, href, Icon }) => (
                    <SocialLink
                      key={label}
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={label}
                    >
                      <Icon aria-hidden="true" focusable="false" />
                    </SocialLink>
                  ))}
                </SocialLinks>
              </Brand>

              <Cols data-cy="editorial-footer-cols">
                {columns.map((col) => (
                  <Col
                    key={col.heading}
                    data-cy={`editorial-footer-col-${col.heading.toLowerCase()}`}
                  >
                    <ColHeading>{col.heading}</ColHeading>
                    {col.links.map((link) => (
                      <ColLink key={link.label} href={withSe3QaOverride(link.href, qaSearch)}>
                        {link.label}
                      </ColLink>
                    ))}
                  </Col>
                ))}
              </Cols>
            </Top>

            <Rule />
          </>
        ) : null}

        <Legal data-cy="editorial-footer-legal">
          {legal ?? (
            <>
              <LegalH>Important Message</LegalH>
              <p>
                In making an investment decision, investors must rely on their own examination of
                the issuer and the terms of the offering, including the merits and risks involved.
                Investments on StartEngine are speculative, illiquid, and involve a high degree of
                risk, including the possible loss of your entire investment.
              </p>
              <p>
                <a href="https://www.startengine.com">www.StartEngine.com</a> is a website owned and
                operated by StartEngine Inc. (&ldquo;StartEngine&rdquo;), which is neither a
                registered broker-dealer, investment advisor nor funding portal.
              </p>
              <p>
                Unless indicated otherwise with respect to a particular issuer, all
                securities-related activity is conducted by regulated affiliates of StartEngine:
                StartEngine Capital LLC, a funding portal{' '}
                <a href="https://www.sec.gov/cgi-bin/browse-edgar?action=getcompany&company=startengine+capital&type=&dateb=&owner=include&count=40">
                  registered here with the US Securities and Exchange Commission (SEC)
                </a>{' '}
                and{' '}
                <a href="https://www.finra.org/about/funding-portals-we-regulate">
                  here as a member of the Financial Industry Regulatory Authority (FINRA)
                </a>
                , or StartEngine Primary LLC (&ldquo;SE Primary&rdquo;), a broker-dealer registered
                with the SEC and FINRA / SIPC. You can review the background of our broker-dealer
                and our investment professionals on{' '}
                <a href="https://brokercheck.finra.org/firm/summary/291387">
                  FINRA&apos;s BrokerCheck here
                </a>
                . StartEngine Secondary is an alternative trading system (ATS) regulated by the SEC
                and operated by SE Primary. SE Primary is a member of SIPC and explanatory brochures
                are available upon request by contacting SIPC at (202) 371-8300.
              </p>
              <p>StartEngine facilitates three types of primary offerings:</p>
              <LegalList>
                <li>
                  Regulation A offerings (JOBS Act Title IV; known as Regulation A+), which are
                  offered to non-accredited and accredited investors alike. These offerings are made
                  through StartEngine Primary, LLC (unless otherwise indicated).
                </li>
                <li>
                  Regulation D offerings (Rule 506(c)), which are offered only to accredited
                  investors. These offerings are made through StartEngine Primary, LLC.
                </li>
                <li>
                  Regulation Crowdfunding offerings (JOBS Act Title III), which are offered to
                  non-accredited and accredited investors alike. These offerings are made through
                  StartEngine Capital, LLC.
                </li>
              </LegalList>
              <p>
                Any securities offered on this website have not been recommended or approved by any
                federal or state securities commission or regulatory authority. StartEngine and its
                affiliates do not provide any investment advice or recommendation and do not provide
                any legal or tax advice concerning any securities.
              </p>
              <p>
                Neither StartEngine nor any of its officers, directors, agents, and employees makes
                any warranty, express or implied, of any kind whatsoever related to the adequacy,
                accuracy, or completeness of any information on this site or the use of information
                on this site.
              </p>
              <p>
                Investing in private company securities is not suitable for all investors. An
                investment in private company securities is highly speculative and involves a high
                degree of risk. It should only be considered a long-term investment. You must be
                prepared to withstand a total loss of your investment. See{' '}
                <a href="/disclaimer">additional general disclosures here</a>.
              </p>
              <p>
                By accessing this site and any pages on this site, you agree to be bound by our{' '}
                <a href="/terms">Terms of use</a> and <a href="/privacy">Privacy Policy</a>, as may
                be amended from time to time without notice or liability.
              </p>
              <LegalSub>StartEngine Marketplace</LegalSub>
              <p>
                StartEngine Marketplace (&ldquo;SE Marketplace&rdquo;) is a website operated by
                StartEngine Primary, LLC (&ldquo;SE Primary&rdquo;), a broker-dealer that is
                registered with the SEC and a member of FINRA and the SIPC.
              </p>
              <p>
                StartEngine Secondary (&ldquo;SE Secondary&rdquo;) is our investor trading platform.
                SE Secondary is an SEC-registered Alternative Trading System (&ldquo;ATS&rdquo;)
                operated by SE Primary that matches orders for buyers and sellers of securities.
              </p>
              <p>
                Even if a security is qualified to be displayed on SE Marketplace, there is no
                guarantee an active trading market for the securities will ever develop, or if
                developed, be maintained. You should assume that you may not be able to liquidate
                your investment for some time or be able to pledge these shares as collateral.
              </p>
              <p>
                Canadian Investors: Investment opportunities posted and accessible through the site
                will not be offered to Canadian resident investors.
              </p>
              <p>
                California Investors Only &ndash; Do Not Sell My Personal Information
                (800-317-2200). StartEngine does not sell personal information. For all customer
                inquiries, please write to{' '}
                <a href="mailto:contact@startengine.com">contact@startengine.com</a>.
              </p>
            </>
          )}
        </Legal>
      </Inner>
    </FooterRoot>
  );
}

export default EditorialFooter;
